/**
 * Estado de cuenta del wallet: los datos los entrega el BE
 * (`GET /accounts/{id}/monthly-statement`) y el FE arma el PDF.
 *
 * `AccountStatement` es lo que consume el PDF; `mapMonthlyStatement` es el
 * único punto que conoce la forma de la respuesta del BE.
 *
 * Montos en centavos, como el resto del wallet. Es PII: no persistir.
 */
import { clientApi, type MonthlyStatement, type MonthlyStatementAddress } from '@tankef/api-client';

export interface AccountStatementClient {
  name: string;
  rfc: string | null;
  /** `null` si la cuenta no tiene CLABE. */
  clabe: string | null;
  clientNumber: string | null;
  bank: string;
  /** Dirección ya partida en renglones (calle / ciudad, estado, CP); vacío si no hay. */
  addressLines: string[];
}

export interface AccountStatementMovement {
  /** ISO 8601 del BE; se muestra en hora de CDMX. */
  occurredAt: string;
  folio: string | null;
  concept: string;
  /** Medio de la operación, p. ej. "Transf. SPEI". */
  method: string;
  creditCents: number;
  debitCents: number;
  balanceCents: number;
}

export interface AccountStatement {
  client: AccountStatementClient;
  currencyCode: string;
  /** Fechas del periodo, `YYYY-MM-DD`. */
  periodStart: string;
  periodEnd: string;
  summary: {
    openingBalanceCents: number;
    totalCreditsCents: number;
    totalDebitsCents: number;
    closingBalanceCents: number;
    averageBalanceCents: number;
  };
  movements: AccountStatementMovement[];
  commissions: {
    chargedCents: number;
    ivaCents: number;
    totalCents: number;
  };
}

export interface AccountStatementParams {
  accountId: string;
  /** `YYYY-MM`; sin él, el BE devuelve el mes en curso hasta hoy. */
  period?: string;
}

/** Texto para un `operation_type` que no trae etiqueta. */
export const UNKNOWN_OPERATION_LABEL = 'Otro';

const join = (parts: (string | null | undefined)[], sep: string) => parts.filter(Boolean).join(sep);

/** "Calle 46 Int. 3, Colonia" / "Ciudad, EDO, C.P. 53370"; cae a `formatted` si no hay partes. */
export function formatStatementAddress(address: MonthlyStatementAddress | null): string[] {
  if (!address) return [];
  const street = join(
    [
      address.street,
      address.exterior_number,
      address.interior_number && `Int. ${address.interior_number}`,
    ],
    ' '
  );
  const line1 = join([street, address.neighborhood], ', ');
  const line2 = join(
    [address.city, address.state_code, address.postal_code && `C.P. ${address.postal_code}`],
    ', '
  );
  const lines = [line1, line2].filter(Boolean);
  if (lines.length > 0) return lines;
  return address.formatted ? [address.formatted] : [];
}

export function mapMonthlyStatement(raw: MonthlyStatement): AccountStatement {
  const { customer, summary, commissions } = raw;
  return {
    client: {
      name: customer.name,
      rfc: customer.rfc,
      clabe: customer.clabe,
      clientNumber: customer.customer_number,
      bank: customer.bank_name,
      addressLines: formatStatementAddress(customer.address),
    },
    currencyCode: raw.account.currency_code,
    periodStart: raw.period.from,
    periodEnd: raw.period.to,
    summary: {
      openingBalanceCents: summary.opening_balance_cents,
      totalCreditsCents: summary.total_deposits_cents,
      totalDebitsCents: summary.total_withdrawals_cents,
      closingBalanceCents: summary.closing_balance_cents,
      averageBalanceCents: summary.average_balance_cents,
    },
    movements: raw.movements.map(m => ({
      occurredAt: m.occurred_at,
      folio: m.folio,
      concept: m.concept,
      method: m.operation_label || UNKNOWN_OPERATION_LABEL,
      creditCents: m.deposit_cents,
      debitCents: m.withdrawal_cents,
      balanceCents: m.balance_after_cents,
    })),
    commissions: {
      chargedCents: commissions.commission_cents,
      ivaCents: commissions.iva_cents,
      totalCents: commissions.total_cents,
    },
  };
}

export async function fetchAccountStatement({
  accountId,
  period,
}: AccountStatementParams): Promise<AccountStatement> {
  return mapMonthlyStatement(await clientApi.accountMonthlyStatement(accountId, period));
}
